import * as THREE from "three";
import { BLOCK, type BlockId, type BlockKind, type VoxelWorld } from "../types";
import { SceneManager } from "./SceneManager";
import { AO_STRENGTH, BLOCK_SIZE } from "../config";
import { ALL_BLOCK_FACES, BLOCK_FACE } from "./Materials";
import { isRenderedFluid, renderFluids } from "./FluidRenderer";

/**
 * Block kinds that DO NOT fully occlude their neighbors. Used by hidden-block
 * culling: a solid block surrounded by 6 solid neighbors is skipped, but if any
 * neighbor is in this set the block is rendered.
 */
const TRANSPARENT_KINDS = new Set<BlockKind>([
    BLOCK.Water,
    BLOCK.Ice,
    BLOCK.Leaves,
    BLOCK.PineLeaves,
    BLOCK.LilyPad,
    BLOCK.FlowerRed,
    BLOCK.FlowerYellow,
    BLOCK.Mushroom,
    BLOCK.Cactus,
    BLOCK.Rock,
    BLOCK.IceSpike,
    BLOCK.TallGrass,
    BLOCK.DeadBush,
    BLOCK.Portal,
    BLOCK.Lava,
]);

const SOLID_OPAQUE = (kind: BlockId): boolean =>
    kind !== BLOCK.Empty && !TRANSPARENT_KINDS.has(kind as BlockKind);

/**
 * 32-bit hash of three integers; returns 0..1.
 */
function hash3(x: number, y: number, z: number): number {
    let h = x * 374761393 + y * 668265263 + z * 2147483647;
    h = (h ^ (h >>> 13)) * 1274126177;
    h = h ^ (h >>> 16);
    return ((h >>> 0) % 1000) / 1000;
}

const dummy = new THREE.Object3D();
const tmpColor = new THREE.Color();

interface FaceDirection {
    bit: number;
    opposite: number;
    dx: number;
    dy: number;
    dz: number;
}

const FACE_DIRECTIONS: FaceDirection[] = [
    { bit: BLOCK_FACE.positiveX, opposite: BLOCK_FACE.negativeX, dx: 1, dy: 0, dz: 0 },
    { bit: BLOCK_FACE.negativeX, opposite: BLOCK_FACE.positiveX, dx: -1, dy: 0, dz: 0 },
    { bit: BLOCK_FACE.positiveY, opposite: BLOCK_FACE.negativeY, dx: 0, dy: 1, dz: 0 },
    { bit: BLOCK_FACE.negativeY, opposite: BLOCK_FACE.positiveY, dx: 0, dy: -1, dz: 0 },
    { bit: BLOCK_FACE.positiveZ, opposite: BLOCK_FACE.negativeZ, dx: 0, dy: 0, dz: 1 },
    { bit: BLOCK_FACE.negativeZ, opposite: BLOCK_FACE.positiveZ, dx: 0, dy: 0, dz: -1 },
];

const faceGeometryCache = new WeakMap<THREE.BufferGeometry, Map<number, THREE.BufferGeometry>>();

export interface PlacedInstance {
    mesh: THREE.InstancedMesh;
    index: number;
    px: number;
    py: number;
    pz: number;
}

function aoFactor(world: VoxelWorld, x: number, y: number, z: number): number {
    let occ = 0;
    if (SOLID_OPAQUE(world.get(x + 1, y, z))) occ++;
    if (SOLID_OPAQUE(world.get(x - 1, y, z))) occ++;
    if (SOLID_OPAQUE(world.get(x, y, z + 1))) occ++;
    if (SOLID_OPAQUE(world.get(x, y, z - 1))) occ++;
    if (SOLID_OPAQUE(world.get(x + 1, y + 1, z))) occ++;
    if (SOLID_OPAQUE(world.get(x - 1, y + 1, z))) occ++;
    if (SOLID_OPAQUE(world.get(x, y + 1, z + 1))) occ++;
    if (SOLID_OPAQUE(world.get(x, y + 1, z - 1))) occ++;
    return 1 - AO_STRENGTH * (occ / 8);
}

export interface RenderInstancedOptions {
    trackInstances?: boolean;
}

export function renderInstanced(
    scene: SceneManager,
    world: VoxelWorld,
    options: RenderInstancedOptions = {},
): PlacedInstance[] {
    const trackInstances = options.trackInstances ?? true;
    const half = (world.size * BLOCK_SIZE) / 2;
    const batches = new Map<
        number,
        { kind: BlockKind; faceMask: number; count: number }
    >();

    const visible: {
        key: number;
        kind: BlockKind;
        x: number;
        y: number;
        z: number;
    }[] = [];
    for (let x = 0; x < world.size; x++) {
        for (let z = 0; z < world.size; z++) {
            const offset = world.columnOffset(x, z);
            for (let y = 0; y < world.height; y++) {
                const id = world.data[offset + y] as BlockId;
                if (id === BLOCK.Empty) continue;
                if (isRenderedFluid(id)) continue;
                const kind = id as BlockKind;
                const faceMask = visibleFaceMask(scene, world, kind, x, y, z);
                if (faceMask === 0) continue;
                const key = (kind << 6) | faceMask;
                visible.push({ key, kind, x, y, z });
                const batch = batches.get(key);
                if (batch) {
                    batch.count++;
                } else {
                    batches.set(key, { kind, faceMask, count: 1 });
                }
            }
        }
    }

    // Pass 2: transparent cuboids are also grouped by their visible-face mask.
    const meshes = new Map<number, THREE.InstancedMesh>();
    batches.forEach(({ kind, faceMask, count }, key) => {
        const entry = scene.materials[kind];
        if (!entry) return;
        const geometry = geometryForFaceMask(scene, entry.geometry, faceMask);
        const mesh = new THREE.InstancedMesh(geometry, entry.material, count);
        mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(count * 3), 3);
        meshes.set(key, mesh);
        scene.group.add(mesh);
    });

    // Pass 3: fill matrices + colors.
    const placed: PlacedInstance[] = [];
    const cursor = new Map<number, number>();
    for (const { key, kind, x, y, z } of visible) {
        const mesh = meshes.get(key);
        if (!mesh) continue;

        const i = cursor.get(key) ?? 0;
        cursor.set(key, i + 1);

        const px = x * BLOCK_SIZE - half + BLOCK_SIZE / 2;
        const py = y * BLOCK_SIZE;
        const pz = z * BLOCK_SIZE - half + BLOCK_SIZE / 2;
        dummy.position.set(px, py, pz);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(1, 1, 1);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        if (trackInstances) placed.push({ mesh, index: i, px, py, pz });

        const entry = scene.materials[kind];
        if (mesh.instanceColor) {
            if (entry.jitter > 0 || SOLID_OPAQUE(kind)) {
                const jitterK =
                    entry.jitter > 0 ? 1 + (hash3(x, y, z) * 2 - 1) * entry.jitter : 1;
                const ao = SOLID_OPAQUE(kind) ? aoFactor(world, x, y, z) : 1;
                tmpColor.copy(entry.material.color).multiplyScalar(jitterK * ao);
            } else {
                tmpColor.setScalar(1);
            }
            mesh.setColorAt(i, tmpColor);
        }
    }

    // Flush updates.
    meshes.forEach((mesh) => {
        mesh.instanceMatrix.needsUpdate = true;
        if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
        // Per-instance geometry already shared & registered; nothing extra to dispose.
    });

    renderFluids(scene, world);

    return placed;
}

function visibleFaceMask(
    scene: SceneManager,
    world: VoxelWorld,
    kind: BlockKind,
    x: number,
    y: number,
    z: number,
): number {
    const entry = scene.materials[kind];
    if (entry.boundaryFaces === 0) return ALL_BLOCK_FACES;

    let mask = ALL_BLOCK_FACES;
    for (const face of FACE_DIRECTIONS) {
        if ((entry.boundaryFaces & face.bit) === 0) continue;
        const neighborKind = world.get(x + face.dx, y + face.dy, z + face.dz);
        if (neighborKind === BLOCK.Empty) continue;
        const neighbor = scene.materials[neighborKind];
        const neighborCoversFace =
            neighbor && (neighbor.boundaryFaces & face.opposite) !== 0;
        const shouldCull = entry.material.transparent
            ? neighborCoversFace
            : neighborCoversFace && SOLID_OPAQUE(neighborKind);
        if (shouldCull) {
            mask &= ~face.bit;
        }
    }
    return mask;
}

function geometryForFaceMask(
    scene: SceneManager,
    source: THREE.BufferGeometry,
    faceMask: number,
): THREE.BufferGeometry {
    if (faceMask === ALL_BLOCK_FACES) return source;

    let geometries = faceGeometryCache.get(source);
    if (!geometries) {
        geometries = new Map();
        faceGeometryCache.set(source, geometries);
    }
    const cached = geometries.get(faceMask);
    if (cached) return cached;

    const index = source.getIndex();
    const normals = source.getAttribute("normal");
    if (!index || !normals) return source;

    const keptIndices: number[] = [];
    for (let i = 0; i < index.count; i += 3) {
        const vertex = index.getX(i);
        const bit = faceBitForNormal(
            normals.getX(vertex),
            normals.getY(vertex),
            normals.getZ(vertex),
        );
        if ((faceMask & bit) === 0) continue;
        keptIndices.push(index.getX(i), index.getX(i + 1), index.getX(i + 2));
    }

    const geometry = source.clone();
    geometry.setIndex(keptIndices);
    geometry.clearGroups();
    geometries.set(faceMask, geometry);
    scene.sharedGeometries.add(geometry);
    return geometry;
}

function faceBitForNormal(nx: number, ny: number, nz: number): number {
    if (nx > 0.5) return BLOCK_FACE.positiveX;
    if (nx < -0.5) return BLOCK_FACE.negativeX;
    if (ny > 0.5) return BLOCK_FACE.positiveY;
    if (ny < -0.5) return BLOCK_FACE.negativeY;
    if (nz > 0.5) return BLOCK_FACE.positiveZ;
    return BLOCK_FACE.negativeZ;
}
